import { queryFirst, queryAll, addClass, removeClass, hasClass, asElement, matchesBreakpoint } from '../domUtil';
import Swiper from 'swiper';
import { Navigation, Pagination, Scrollbar, Thumbs, Mousewheel, FreeMode, Controller, EffectFade } from 'swiper/modules';
import { sliderPaginationTemplate } from '../templates';
import { EVENT_KEY_ENTER, EVENT_KEY_SPACE } from '../constants';
import { syncPdpGalleryVideos } from './pdpVideoInView';

let scrollTimer = null;
const scrollClassName = 'm-scroll';
const EXPANDED_CLASS = 'm-expanded';
const GALLERY_EXPAND_TRANSITION_MS = 550;
const SET_GALLERY_VIDEO_FOCUS_SELECTOR = '.play-video, .mute-video, .caption-video, .video-link-entire';
const pdpMainImages = queryFirst('.pdp .primary-images');
const pdpImageExpand = queryFirst('.js-pdp-images-expand', pdpMainImages);

/**
 * Init PDP Gallery swiper slider
 * @param {DOMElement} gallery PDP galery
 * @returns {Swiper} Swipper instance
 */
function initGallery(gallery) {
    return new Swiper(gallery, {
        modules: [Scrollbar],
        simulateTouch: false,
        draggable: false,
        loop: false,
        centerInsufficientSlides: true,
        slidesPerView: 1,
        watchSlidesProgress: false,
        scrollbar: {
            el: queryFirst('.js-swiper-scrollbar', gallery)
        },
        breakpoints: {
            768: { slidesPerView: 'auto', enabled: false }
        },
        on: {
            resize(swiper) {
                swiper.update();
                initTabIndexes(swiper);
                preloadImages(swiper);
                setVisibleHeight();
            },
            slideNextTransitionStart: (swiper) => {
                const customEvent = new CustomEvent('swiperSlideChange', {
                    detail: {
                        direction: 'left'
                    }
                });
                swiper.el.dispatchEvent(customEvent);
                preloadImages(swiper);
            },
            slidePrevTransitionStart: (swiper) => {
                const customEvent = new CustomEvent('swiperSlideChange', {
                    detail: {
                        direction: 'right'
                    }
                });
                swiper.el.dispatchEvent(customEvent);
                preloadImages(swiper);
            },
            slideChangeTransitionEnd: (swiper) => {
                syncPdpGalleryVideos(swiper.el);
            },
            afterInit: (swiper) => {
                preloadImages(swiper);
                initTabIndexes(swiper);
                setVisibleHeight();
            }
        }
    });
}

/**
 * Init PDP Gallery on Product set PDP includes
 * @param {DOMElement} gallery PDP galery
 * @returns {Swiper} Swipper instance
 */
function initPDPGallerySet(gallery) {
    const swiper = new Swiper(gallery, {
        modules: [Navigation, Pagination, Scrollbar],
        simulateTouch: false,
        loop: false,
        centerInsufficientSlides: true,
        slidesPerView: 1.05,
        // Required for swiper.visibleSlides + slide visibility classes; tabindex sync uses visibleSlides only.
        watchSlidesProgress: true,
        pagination: {
            el: queryFirst('.js-swiper-pagination', gallery),
            type: 'fraction',
            renderFraction: sliderPaginationTemplate
        },
        navigation: {
            nextEl: queryFirst('.js-swiper-button-next', gallery),
            prevEl: queryFirst('.js-swiper-button-prev', gallery)
        },
        scrollbar: {
            el: queryFirst('.js-swiper-scrollbar', gallery)
        },
        breakpoints: {
            768: {
                loop: false,
                slidesPerView: 2,
                watchSlidesProgress: true
            },
            1025: {
                loop: true,
                slidesPerView: 2,
                watchSlidesProgress: true
            }
        },
        on: {
            resize(swiper) {
                swiper.update();
                swiper.pagination.update();
                updatePdpSetGalleryTabIndexes(swiper);
            },
            slideNextTransitionStart: (swiper) => {
                const customEvent = new CustomEvent('swiperSlideChange', {
                    detail: {
                        direction: 'left'
                    }
                });
                swiper.el.dispatchEvent(customEvent);
                preloadImages(swiper);
            },
            slidePrevTransitionStart: (swiper) => {
                const customEvent = new CustomEvent('swiperSlideChange', {
                    detail: {
                        direction: 'right'
                    }
                });
                swiper.el.dispatchEvent(customEvent);
                preloadImages(swiper);
            },
            slideChangeTransitionEnd: (swiper) => {
                updatePdpSetGalleryTabIndexes(swiper);
                syncPdpGalleryVideos(swiper.el);
            },
            afterInit: (swiper) => {
                preloadImages(swiper);
                updatePdpSetGalleryTabIndexes(swiper);
            }
        }
    });

    swiper.on('navigationNext', () => { swiper._navFrom = 'next'; });
    swiper.on('navigationPrev', () => { swiper._navFrom = 'prev'; });
    swiper.on('slideNextTransitionEnd', () => {
        if (swiper._navFrom !== 'next') return;
        const { nextEl: nextBtn, prevEl: prevBtn } = swiper.navigation || {};
        (nextBtn?.classList.contains('swiper-button-disabled') ? prevBtn : nextBtn)?.focus();
        swiper._navFrom = null;
    });
    swiper.on('slidePrevTransitionEnd', () => {
        if (swiper._navFrom !== 'prev') return;
        const { nextEl: nextBtn, prevEl: prevBtn } = swiper.navigation || {};
        (prevBtn?.classList.contains('swiper-button-disabled') ? nextBtn : prevBtn)?.focus();
        swiper._navFrom = null;
    });

    return swiper;
}
/**
 * Init PDP Zoom Gallery swiper slider
 * @param {DOMElement} lightbox lightbox wrapper
 * @returns {Swiper} Swipper instance
 */
function initZoomGallery(lightbox) {
    const swiperDescription = new Swiper(queryFirst('.js-swiper-modal-description', lightbox), {
        modules: [EffectFade],
        effect: 'fade'
    });

    const swiperThumb = new Swiper(queryFirst('.js-swiper-modal-thumbs', lightbox), {
        slidesPerView: 'auto'
    });

    const mainSwiper = new Swiper(queryFirst('.js-swiper-modal', lightbox), {
        modules: [Navigation, Thumbs, Mousewheel, Pagination, Scrollbar, FreeMode, Controller],
        freeMode: false,
        autoHeight: false,
        direction: 'horizontal',
        loop: false,
        enabled: false,
        slidesPerView: 'auto',
        watchSlidesProgress: true,
        updateOnWindowResize: false, // disables auto-update on resize/orientationchange
        observer: false, // disables mutation observer
        resizeObserver: false,
        navigation: {
            nextEl: queryFirst('.js-swiper-modal-button-next', lightbox)
        },
        pagination: {
            el: queryFirst('.js-swiper-modal-pagination', lightbox),
            type: 'fraction',
            renderFraction: sliderPaginationTemplate
        },
        scrollbar: {
            el: queryFirst('.js-swiper-modal-scrollbar', lightbox)
        },
        controller: {
            control: swiperDescription
        },
        thumbs: {
            swiper: swiperThumb
        },
        mousewheel: {
            enabled: true,
            sensitivity: 0.75
        },
        breakpoints: {
            0: {
                direction: 'horizontal'
            },
            1025: {
                direction: 'vertical',
                speed: 1000,
                freeMode: {
                    enabled: true
                }
            }
        },
        on: {
            scroll: (swiper) => {
                const swiperEl = swiper.el;

                addClass(swiperEl, scrollClassName);

                if (scrollTimer !== null) {
                    clearTimeout(scrollTimer);
                }

                scrollTimer = setTimeout(() => {
                    removeClass(swiperEl, scrollClassName);
                }, 150);
            }
        }
    });

    // Add keyboard accessibility for thumbs
    const thumbElements = queryAll('.js-swiper-modal-thumbs .swiper-slide', lightbox);

    thumbElements.forEach((thumb, index) => {
        thumb.addEventListener('keydown', (event) => {
            if (event.key === EVENT_KEY_ENTER) {
                mainSwiper.slideTo(index);
                swiperDescription.slideTo(index);
            }
        });
    });

    return mainSwiper;
}

/**
 * Init PDP "Show more images" expand button.
 *
 * @param  {HTMLElement} pdpMainImages Container with primary images.
 * @param  {HTMLElement} pdpImageExpand Expand button inside container.
 * @return {void}
 */
function initPdpExpand(pdpMainImages, pdpImageExpand) {
    if (!pdpMainImages || !pdpImageExpand) return;

    const imgs = queryAll('.swiper-slide .swiper-lazy-image', pdpMainImages);

    setVisibleHeight();
    removeClass(imgs, 'swiper-lazy-image');

    function expand() {
        addClass(pdpMainImages, EXPANDED_CLASS);

        const afterLayout = () => {
            const nextImage = queryFirst('.swiper-slide:nth-of-type(5) > button', pdpMainImages);
            if (!nextImage) return;

            nextImage.focus();
        };

        // two frames to await for reflow:
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                afterLayout();
                syncPdpGalleryVideos(pdpMainImages);
            });
        });

        window.setTimeout(() => {
            syncPdpGalleryVideos(pdpMainImages);
        }, GALLERY_EXPAND_TRANSITION_MS);
    }

    pdpImageExpand.addEventListener('click', expand);

    pdpImageExpand.addEventListener('keydown', (e) => {
        if (e.key === EVENT_KEY_ENTER || e.key === EVENT_KEY_SPACE) {
            e.preventDefault();
            expand();
        }
    });
}

/**
 * Preload images in currently active, next, previous and visible Swiper slides.
 * Removes the "swiper-lazy-image" class to trigger loading.
 *
 * @param  {HTMLElement|jQuery|Object} swiper Swiper container or instance.
 *     Can be a DOM element, jQuery object or Swiper instance.
 * @return {void}
 */
function preloadImages(swiper) {
    const scope = asElement(swiper);

    const imgs = queryAll(
        `.swiper-slide-active .swiper-lazy-image,
        .swiper-slide-next .swiper-lazy-image,
        .swiper-slide-prev .swiper-lazy-image`, scope);
    removeClass(imgs, 'swiper-lazy-image');
}

/**
 * Initialize tab indexes inside Swiper gallery.
 * Removes tabindex from modal trigger links so they remain keyboard-accessible.
 *
 * @param  {HTMLElement|jQuery|Object} swiper Swiper container or instance.
 *     Can be a DOM element, jQuery object or Swiper instance.
 * @return {void}
 */
function initTabIndexes(swiper) {
    const scope = asElement(swiper);

    const galleryTriggers = queryAll('a[data-toggle="modal"]', scope);

    galleryTriggers.forEach(link => link.setAttribute('tabindex', '0'));
}

/**
 * Product set PDP: only lightbox triggers and video controls on Swiper-visible slide(s) participate in tab order.
 *
 * @param {import('swiper').Swiper} swiper
 */
function updatePdpSetGalleryTabIndexes(swiper) {
    const slides = Array.from(swiper.slides || []);
    if (!slides.length) return;

    slides.forEach((slide) => {
        const isVisible = hasClass(slide, 'swiper-slide-visible');
        slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

        const trigger = queryFirst('.pdp-gallery-popup-trigger', slide);
        if (trigger) {
            trigger.setAttribute('tabindex', isVisible ? '0' : '-1');
        }

        queryAll(SET_GALLERY_VIDEO_FOCUS_SELECTOR, slide).forEach(focusableEl => {
            focusableEl.setAttribute('tabindex', isVisible ? '0' : '-1');
        });
    });
}

/**
 * Call after main gallery slides are replaced (e.g. variation change) so tabindex / aria-hidden match visible slides.
 * Product set PDP only; no-op if not a set gallery or Swiper not ready.
 *
 * @param {HTMLElement} carouselEl - `.js-swiper-main` element
 */
export function refreshPdpSetGalleryTabIndexes(carouselEl) {
    if (!carouselEl || typeof carouselEl.closest !== 'function') return;
    if (!carouselEl.closest('.product-set-detail')) return;
    if (!hasClass(carouselEl, 'js-swiper-main')) return;
    const sw = carouselEl.swiper;
    if (!sw) return;
    requestAnimationFrame(() => {
        sw.update();
        requestAnimationFrame(() => {
            updatePdpSetGalleryTabIndexes(sw);
        });
    });
}

/**
 * Calculate and set max-height for PDP main images container
 * based on its current width and a 4:5 aspect ratio.
 *
 * @return {void}
 */
function setVisibleHeight() {
    if (!pdpMainImages || !pdpImageExpand) return;
    if (!matchesBreakpoint('md')) return;
    if (hasClass(pdpMainImages, EXPANDED_CLASS)) return;

    const width = pdpMainImages.getBoundingClientRect().width;
    const height = Math.round((width * 5) / 4); // based on 4:5 aspect ratio

    pdpMainImages.style.maxHeight = height + 'px';

    const imgs = queryAll('.swiper-lazy-image', pdpMainImages);

    removeClass(imgs, 'swiper-lazy-image');
}

/**
 * Anchor jump to #stylitics-widget.
 * Smooth scroll via JS; focus target after layout.
 */
function smoothScrollAndFocus(target, event) {
    if (!target) return;

    if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
    }

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = prefersReduced ? 'auto' : 'smooth';

    target.scrollIntoView({ behavior, block: 'start', inline: 'nearest' });

    // Apply focus only when navigation was triggered from keyboard
    const isKeyboard = event instanceof KeyboardEvent;

    if (isKeyboard) {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                try {
                    target.focus({ preventScroll: true });
                } catch {
                    target.focus();
                }
            });
        });
    }
}
/**
 * Initialize Stylitics jumplink functionality.
 * Listens for clicks on links with class "stylitics-jumplink" and smooth-scrolls to #stylitics-widget.
 */
function initStyliticsJumpLink() {
    const TARGET_ID = 'stylitics-widget';
    const LINK_SELECTOR = `a.stylitics-jumplink[href="#${TARGET_ID}"]`;

    // Click handler (mouse or touch)
    document.addEventListener('click', async (e) => {
        const link = e.target.closest(LINK_SELECTOR);
        if (!link) return;

        e.preventDefault();
        const target = await waitForElementById(TARGET_ID);
        smoothScrollAndFocus(target, e);
        // Trigger click analytics event for Stylitics
        const styliticsTarget = target?.closest('#stylitics-pdp-api-widget');
        styliticsTarget?.dispatchEvent(new CustomEvent('stylitics:jumplink:click'));
    });

    // Keyboard handler (Enter or Space on the link)
    document.addEventListener('keydown', async (e) => {
        const link = e.target.closest(LINK_SELECTOR);
        if (!link || (e.key !== EVENT_KEY_ENTER && e.key !== EVENT_KEY_SPACE)) return;

        e.preventDefault();
        const target = await waitForElementById(TARGET_ID);
        smoothScrollAndFocus(target, e);
        // Trigger click analytics event for Stylitics
        const styliticsTarget = target?.closest('#stylitics-pdp-api-widget');
        styliticsTarget?.dispatchEvent(new CustomEvent('stylitics:jumplink:click'));
    });
}

function waitForElementById(id) {
    return new Promise((resolve) => {
        let el = document.getElementById(id);
        if (el) return resolve(el);

        const obs = new MutationObserver(() => {
            el = document.getElementById(id);
            if (el) {
                obs.disconnect();
                resolve(el);
            }
        });

        obs.observe(document.documentElement, {
            childList: true,
            subtree: true
        });
    });
}

initStyliticsJumpLink();

queryAll('.js-outer-pdp-container.pdp .js-swiper-main').forEach(initGallery);
queryAll('.product-set-detail .js-swiper-main').forEach(initPDPGallerySet);
queryAll('.js-pdp-lightbox-modal').forEach(initZoomGallery);

initPdpExpand(pdpMainImages, pdpImageExpand);
setVisibleHeight();

['resize', 'orientationchange'].forEach(event =>
    window.addEventListener(event, () => {
        if (!matchesBreakpoint('md')) {
            pdpMainImages?.removeAttribute('style');
            return;
        }

        const mainCarousel = queryFirst('.js-outer-pdp-container.pdp .js-swiper-main');
        const { swiper } = mainCarousel || {};

        if (swiper) {
            swiper.slideTo(0);
        }
    })
);

requestAnimationFrame(() => {
    requestAnimationFrame(() => {
        syncPdpGalleryVideos();
    });
});

